import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="privacy-page">
      <SEO 
        title="Privacy Policy"
        description="Learn how Branding Yard collects, uses, and protects your personal information."
        url="/privacy"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Privacy Policy' }
        ]}
      />
      
      {/* Header */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight text-white">
            Privacy Policy
          </h1>
          <p className="mt-4 text-[#A3A3A3]">
            Last updated: February 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl prose prose-lg">
            
            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Introduction</h2>
              <p className="text-[#737373] leading-relaxed">
                Branding Yard ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase from us.
              </p>
              <p className="text-[#737373] leading-relaxed mt-4">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Information We Collect</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                We may collect information about you in a variety of ways. The information we may collect includes:
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Personal Data</h3>
              <p className="text-[#737373] leading-relaxed">
                Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information, such as your age, gender, hometown, and interests, that you voluntarily give to us when you register with the site or when you choose to participate in various activities related to the site, such as making purchases.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Derivative Data</h3>
              <p className="text-[#737373] leading-relaxed">
                Information our servers automatically collect when you access the site, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the site.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Financial Data</h3>
              <p className="text-[#737373] leading-relaxed">
                Financial information, such as data related to your payment method (e.g., valid credit card number, card brand, expiration date) that we may collect when you purchase, order, return, exchange, or request information about our services from the site. We store only very limited, if any, financial information that we collect.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Use of Your Information</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the site to:
              </p>
              <ul className="list-disc list-inside text-[#737373] space-y-2">
                <li>Create and manage your account</li>
                <li>Process your orders and deliver products</li>
                <li>Email you regarding your account or order</li>
                <li>Send you promotional information and marketing communications</li>
                <li>Fulfill and manage purchases, orders, payments, and other transactions</li>
                <li>Improve our website and product offerings</li>
                <li>Monitor and analyze usage and trends to improve your experience</li>
                <li>Notify you of updates to our website</li>
                <li>Respond to customer service requests</li>
                <li>Prevent fraudulent transactions and protect against illegal activity</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Disclosure of Your Information</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">By Law or to Protect Rights</h3>
              <p className="text-[#737373] leading-relaxed">
                If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
              </p>
              <h3 className="font-semibold text-[#0A0A0A] mt-6 mb-2">Third-Party Service Providers</h3>
              <p className="text-[#737373] leading-relaxed">
                We may share your information with third parties that perform services for us or on our behalf, including payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Security of Your Information</h2>
              <p className="text-[#737373] leading-relaxed">
                We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-[#737373] leading-relaxed">
                We may use cookies, web beacons, tracking pixels, and other tracking technologies on the site to help customize the site and improve your experience. When you access the site, your personal information is not collected through the use of tracking technology. Most browsers are set to accept cookies by default. You can remove or reject cookies, but be aware that such action could affect the availability and functionality of the site.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Your Rights</h2>
              <p className="text-[#737373] leading-relaxed mb-4">
                You may at any time review or change the information in your account or terminate your account by:
              </p>
              <ul className="list-disc list-inside text-[#737373] space-y-2">
                <li>Logging into your account settings and updating your account</li>
                <li>Contacting us using the contact information provided below</li>
              </ul>
              <p className="text-[#737373] leading-relaxed mt-4">
                Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, some information may be retained in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our Terms of Service, and/or comply with legal requirements.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">Contact Us</h2>
              <p className="text-[#737373] leading-relaxed">
                If you have questions or comments about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-[#FAFAFA] border border-[#E5E5E5]">
                <p className="font-semibold text-[#0A0A0A]">Branding Yard</p>
                <p className="text-[#737373] mt-2">Email: info@brandingyard.co.zw</p>
                <p className="text-[#737373]">Harare, Zimbabwe</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
